import { useState, useEffect, useMemo } from 'react';
import { Link } from '@tanstack/react-router';

const API = import.meta.env.VITE_API_URL || '/api';

type InvoiceStatus = 'issued' | 'paid' | 'cancelled';

interface Invoice {
  id: string;
  number: string;
  type?: string;
  issuedAt: string;
  paidAt?: string | null;
  customerId?: string | null;
  customerName?: string | null;
  saleId?: string | null;
  subtotal: number | string;
  discount?: number | string;
  taxAmount: number | string;
  total: number | string;
  status: InvoiceStatus;
}

interface MonthSummary {
  count: number;
  totalSubtotal: number | string | null;
  totalTax: number | string | null;
  totalTotal: number | string | null;
}

const statusColors: Record<InvoiceStatus, string> = {
  issued: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  paid: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  cancelled: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
};

const statusLabels: Record<InvoiceStatus, string> = {
  issued: 'Emitida',
  paid: 'Pagada',
  cancelled: 'Anulada',
};

function formatMoney(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isFinite(num)) return '$0.00';
  return `$${num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value: string | undefined): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('es-AR');
}

function getMonthRange(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: first.toISOString().split('T')[0],
    to: last.toISOString().split('T')[0],
  };
}

export function Invoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | InvoiceStatus>('all');
  const [monthTotal, setMonthTotal] = useState<number | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);

  const token = localStorage.getItem('accessToken');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };

  async function loadInvoices() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/invoices`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cargar las facturas');
      }
      const data = (await res.json()) as { data?: Invoice[] };
      setInvoices(data.data || []);
    } catch (e: any) {
      console.error('Invoices load error:', e);
      setError(e.message || 'Error al cargar las facturas');
    } finally {
      setLoading(false);
    }
  }

  async function loadMonthSummary() {
    try {
      const { from, to } = getMonthRange();
      const res = await fetch(`${API}/invoices/summary?from=${from}&to=${to}`, { headers });
      if (res.ok) {
        const data = (await res.json()) as { data?: MonthSummary };
        const total = data.data?.totalTotal;
        setMonthTotal(typeof total === 'string' ? parseFloat(total) : typeof total === 'number' ? total : null);
      } else {
        setMonthTotal(null);
      }
    } catch {
      setMonthTotal(null);
    }
  }

  useEffect(() => {
    loadInvoices();
    loadMonthSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function markPaid(id: string) {
    setActionBusy(id);
    try {
      const res = await fetch(`${API}/invoices/${id}/mark-paid`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al marcar como pagada');
      }
      await loadInvoices();
      await loadMonthSummary();
    } catch (e: any) {
      setError(e.message || 'Error al marcar como pagada');
    } finally {
      setActionBusy(null);
    }
  }

  async function downloadPDF(id: string, number: string) {
    setActionBusy(`pdf-${id}`);
    try {
      const res = await fetch(`${API}/invoices/${id}/pdf`, { headers });
      if (!res.ok) throw new Error('Error al descargar el PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura-${number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || 'Error al descargar el PDF');
    } finally {
      setActionBusy(null);
    }
  }

  async function invoiceRemito(id: string | null | undefined, number: string) {
    if (!id) return;
    setActionBusy(`inv-${id}`);
    try {
      const res = await fetch(`${API}/sales/${id}/invoice`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al facturar el remito');
      }
      setError(`Remito ${number} facturado. Se generó la factura.`);
      await loadInvoices();
      await loadMonthSummary();
    } catch (e: any) {
      setError(e.message || 'Error al facturar el remito');
    } finally {
      setActionBusy(null);
    }
  }

  const isRemito = (inv: Invoice) => inv.type === 'remito';

  const counts = useMemo(() => {
    return {
      all: invoices.length,
      issued: invoices.filter(i => i.status === 'issued').length,
      paid: invoices.filter(i => i.status === 'paid').length,
      cancelled: invoices.filter(i => i.status === 'cancelled').length,
    };
  }, [invoices]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return invoices.filter((inv) => {
      const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
      if (!matchesStatus) return false;
      if (!term) return true;
      const number = inv.number?.toLowerCase() || '';
      const client = (inv.customerName || 'Consumidor Final').toLowerCase();
      return number.includes(term) || client.includes(term);
    });
  }, [invoices, search, statusFilter]);

  const chips: { key: 'all' | InvoiceStatus; label: string }[] = [
    { key: 'all', label: 'Todas' },
    { key: 'issued', label: 'Emitidas' },
    { key: 'paid', label: 'Pagadas' },
    { key: 'cancelled', label: 'Anuladas' },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Facturas</h1>
          {monthTotal !== null && monthTotal > 0 && (
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1">
              Total facturado del mes: <span className="font-semibold text-gray-900">{formatMoney(monthTotal)}</span>
            </p>
          )}
        </div>
        <Link
          to="/pos"
          className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-5 py-4 font-bold text-base sm:text-lg shadow transition-colors min-h-[56px]"
        >
          <span className="text-xl">🛒</span>
          Nueva Venta
        </Link>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 p-4 rounded-lg mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-sm font-medium">⚠️ {error}</span>
          <button
            onClick={() => { loadInvoices(); loadMonthSummary(); }}
            className="px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-medium hover:bg-red-800 transition-colors min-h-[44px]"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Search and filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <input
          type="text"
          placeholder="🔍 Buscar por número o cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
          aria-label="Buscar facturas"
        />
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por estado">
          {chips.map((chip) => {
            const active = statusFilter === chip.key;
            return (
              <button
                key={chip.key}
                onClick={() => setStatusFilter(chip.key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors min-h-[44px] ${
                  active
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200'
                }`}
                aria-pressed={active}
              >
                {chip.label} <span className="ml-1 opacity-80">({counts[chip.key]})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <div className="inline-block h-8 w-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3" />
          <p className="text-gray-600 dark:text-gray-300 font-medium">Cargando facturas...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-gray-600 dark:text-gray-300 font-medium">
            {search || statusFilter !== 'all'
              ? 'No se encontraron facturas con ese filtro.'
              : 'Aún no hay facturas. Generá una venta para crear la primera.'}
          </p>
          {!search && statusFilter === 'all' && (
            <Link
              to="/pos"
              className="inline-flex items-center justify-center gap-2 mt-4 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors min-h-[44px]"
            >
              <span>🛒</span> Ir al Punto de Venta
            </Link>
          )}
        </div>
      )}

      {/* Mobile cards */}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 lg:hidden gap-3 mb-6">
          {filtered.map((inv) => {
            const busy = actionBusy === inv.id;
            const pdfBusy = actionBusy === `pdf-${inv.id}`;
            return (
              <div key={inv.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="font-mono font-semibold text-gray-900">{isRemito(inv) ? `📋 ${inv.number}` : `🧾 ${inv.number}`}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{formatDate(inv.issuedAt)}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[inv.status] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}>
                    {isRemito(inv) ? 'Remito' : statusLabels[inv.status] || inv.status}
                  </span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">
                  <span className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Cliente:</span>{' '}
                  {inv.customerName || 'Consumidor Final'}
                </p>
                {Number(inv.discount) > 0 && (
                  <p className="text-sm text-red-600 dark:text-red-400 mb-1">
                    Descuento: <span className="font-semibold">-{formatMoney(inv.discount)}</span>
                  </p>
                )}
                <p className="text-lg font-bold text-gray-900 mb-3">{formatMoney(inv.total)}</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => downloadPDF(inv.id, inv.number)}
                    disabled={pdfBusy}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-70 min-h-[44px]"
                  >
                    {pdfBusy ? 'Descargando...' : '📄 Descargar PDF'}
                  </button>
                  {isRemito(inv) ? (
                    <button
                      onClick={() => invoiceRemito(inv.saleId, inv.number)}
                      disabled={busy}
                      className="flex-1 px-4 py-3 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 transition-colors disabled:opacity-70 min-h-[44px]"
                    >
                      {busy ? 'Guardando...' : '🧾 Facturar'}
                    </button>
                  ) : inv.status === 'issued' ? (
                    <button
                      onClick={() => markPaid(inv.id)}
                      disabled={busy}
                      className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-70 min-h-[44px]"
                    >
                      {busy ? 'Guardando...' : '✓ Marcar Pagada'}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop table */}
      {!loading && filtered.length > 0 && (
        <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">N°</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Fecha</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Cliente</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Subtotal</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Descuento</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">IVA</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Total</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">Estado</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => {
                const busy = actionBusy === inv.id;
                const pdfBusy = actionBusy === `pdf-${inv.id}`;
                return (
                  <tr key={inv.id} className="border-t hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 font-mono text-sm">
                      {isRemito(inv) ? <span className="text-orange-600 dark:text-orange-400">📋 {inv.number}</span> : <span className="text-gray-900 dark:text-gray-100">🧾 {inv.number}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{formatDate(inv.issuedAt)}</td>
                    <td className="px-4 py-3 text-sm">{inv.customerName || 'Consumidor Final'}</td>
                    <td className="px-4 py-3 text-right text-sm">{formatMoney(inv.subtotal)}</td>
                    <td className="px-4 py-3 text-right text-sm">{Number(inv.discount) > 0 ? <span className="text-red-600 dark:text-red-400">-{formatMoney(inv.discount)}</span> : <span>-</span>}</td>
                    <td className="px-4 py-3 text-right text-sm">{isRemito(inv) ? <span className="text-gray-400">-</span> : formatMoney(inv.taxAmount)}</td>
                    <td className="px-4 py-3 text-right font-bold text-sm">{formatMoney(inv.total)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[inv.status] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}>
                        {isRemito(inv) ? 'Remito' : statusLabels[inv.status] || inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => downloadPDF(inv.id, inv.number)}
                          disabled={pdfBusy}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors disabled:opacity-70 min-h-[44px]"
                        >
                          {pdfBusy ? '...' : '📄 PDF'}
                        </button>
                        {isRemito(inv) ? (
                          <button
                            onClick={() => invoiceRemito(inv.saleId, inv.number)}
                            disabled={busy}
                            className="px-3 py-2 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 transition-colors disabled:opacity-70 min-h-[44px]"
                          >
                            {busy ? '...' : '🧾 Facturar'}
                          </button>
                        ) : inv.status === 'issued' ? (
                          <button
                            onClick={() => markPaid(inv.id)}
                            disabled={busy}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 transition-colors disabled:opacity-70 min-h-[44px]"
                          >
                            {busy ? '...' : '✓ Pagada'}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary footer */}
      {!loading && filtered.length > 0 && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
          {filtered.length} factura{filtered.length !== 1 ? 's' : ''}{' '}
          {statusFilter !== 'all' ? `(${statusLabels[statusFilter].toLowerCase()})` : ''}
        </p>
      )}
    </div>
  );
}
